import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { useModalsDialogsContext } from './modals-dialogs-context';
/**
 * The hook to render a custom right-anchored Modern Famly drawer.
 *
 * Mirrors {@link useModal}, but renders the lazy content inside an MF `Drawer`
 * (`@famly/mf_navigation_drawer`) instead of a centered modal. Use this when the
 * UX should be a slide-in drawer / side panel, so the slide-in-from-the-right UX
 * is preserved instead of becoming a centered modal.
 *
 * @param options see {@link DrawerOptions}
 * @returns an object containing functions to show and hide the drawer
 */
export function useDrawer(options) {
    const { showModalDialog, hideModalDialog } = useModalsDialogsContext();
    const id = React.useId();
    const handleClose = React.useCallback(() => {
        var _a;
        (_a = options.onClose) === null || _a === void 0 ? void 0 : _a.call(options);
        hideModalDialog(id);
    }, [hideModalDialog, id, options]);
    const show = React.useCallback((props) => {
        var _a;
        // onClose calls the context to remove the drawer from the stack
        const Lazy = React.lazy(options.lazyChildren);
        showModalDialog({
            id,
            type: 'drawer',
            title: options.title,
            maxWidth: options.maxWidth,
            onClose: handleClose,
            element: (_jsx(React.Suspense, { fallback: (_a = options.skeleton) !== null && _a !== void 0 ? _a : null, children: _jsx(Lazy, { ...props, onClose: handleClose }) })),
        });
    }, [handleClose, id, options, showModalDialog]);
    return React.useMemo(() => ({
        showDrawer: show,
        hideDrawer: () => hideModalDialog(id),
    }), [hideModalDialog, show, id]);
}
